import { useState, useEffect } from 'react';
import styles from './Navbar.module.css';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Search } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import SearchOverlay from './SearchOverlay';

const LOGO_PATH = '/logo.png';

const navLinks = [
  { name: 'Home', path: '/' },
  { name: 'About', path: '/about' },
  { name: 'Services', path: '/services' },
  { name: 'Our Work', path: '/our-work' },
  { name: 'Contact', path: '/contact' },
];

const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const location = useLocation();

  // Handle scroll effect for navbar
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > (window.innerHeight * 0.2));
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Handle body overflow when search is open
  useEffect(() => {
    if (isSearchOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }

    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isSearchOpen]);

  return (
    <>
      <header 
        className={`${styles.header} w-full transition-all duration-300 ${
          scrolled || isMenuOpen 
            ? 'bg-dark-gray/90 backdrop-blur-md' 
            : 'bg-transparent'
        }`}
      >
        <div className="container mx-auto px-4 py-2 md:py-3">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <Link to="/" className="flex items-center">
              <div className="relative mr-2">
                <img 
                  src={LOGO_PATH} 
                  alt="Sam Ojo Media" 
                  className="h-12 md:h-14 w-auto -mt-1 scale-110"
                />
              </div>
              <span className="text-xl md:text-2xl font-bold">
                <span className="text-burnt-orange">SAMFREN</span> OJO{' '}
                <span className="text-white">MEDIA</span>
              </span>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center space-x-8">
              {navLinks.map((link) => (
                <div key={link.path} className="relative">
                  <Link
                    to={link.path}
                    className={`text-sm font-medium transition-colors ${
                      location.pathname === link.path 
                        ? 'text-burnt-orange' 
                        : 'text-white hover:text-burnt-orange'
                    }`}
                  >
                    {link.name}
                  </Link>
                  {location.pathname === link.path && (
                    <motion.span
                      layoutId="activeNavLink"
                      className="absolute left-0 -bottom-1 w-full h-0.5 bg-burnt-orange"
                      transition={{
                        type: 'spring',
                        stiffness: 500,
                        damping: 30
                      }}
                    />
                  )}
                </div>
              ))}
              <button
                onClick={() => setIsSearchOpen(true)}
                className="text-white hover:text-burnt-orange transition-colors ml-4 p-1"
                aria-label="Open search"
              >
                <Search size={20} />
              </button>
            </nav>

            {/* Mobile/Tablet menu button */}
            <div className="flex items-center space-x-4 lg:hidden">
              <button
                onClick={() => setIsSearchOpen(true)}
                className="text-white hover:text-burnt-orange transition-colors p-1"
                aria-label="Open search"
              >
                <Search size={20} />
              </button>
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="text-white hover:text-burnt-orange transition-colors p-1"
                aria-label="Toggle menu"
                // @ts-ignore - React 19 type checking is too strict for this case
                aria-expanded={isMenuOpen}
                type="button"
              >
                {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
              </button>
            </div>
          </div>

          {/* Mobile/Tablet menu */}
          <AnimatePresence>
            {isMenuOpen && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.3 }}
                className="lg:hidden overflow-hidden"
              >
                <nav className="flex flex-col space-y-4 py-4">
                  {navLinks.map((link) => (
                    <Link
                      key={link.path}
                      to={link.path}
                      className={`text-base font-medium px-2 py-1 transition-colors ${
                        location.pathname === link.path
                          ? 'text-burnt-orange'
                          : 'text-white hover:text-burnt-orange'
                      }`}
                      onClick={() => setIsMenuOpen(false)}
                    >
                      {link.name}
                    </Link>
                  ))}
                  <button
                    onClick={() => {
                      setIsMenuOpen(false);
                      setIsSearchOpen(true);
                    }}
                    className="flex items-center text-white hover:text-burnt-orange transition-colors px-2 py-1 text-left"
                  >
                    <Search size={18} className="mr-2" />
                    Search
                  </button>
                </nav>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </header>

      {/* Search Overlay */}
      <SearchOverlay isOpen={isSearchOpen} onClose={() => setIsSearchOpen(false)} />
    </>
  );
};

export default Navbar;
